import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { returnService } from "../../services/returnService";
import { orderService } from "../../services/orderService";
import { toast } from "react-toastify";

// Async Thunks for User Return Operations

/**
 * Fetch user's return requests
 */
export const fetchUserReturns = createAsyncThunk(
  "returnsUser/fetchUserReturns",
  async ({ page = 1, limit = 10, status }, { rejectWithValue }) => {
    try {
      console.log("fetchUserReturns thunk called with:", {
        page,
        limit,
        status,
      });
      const data = await returnService.getUserReturns(page, limit, status);
      console.log("fetchUserReturns success, data:", data);
      return data; // { returns: [], pagination: {} }
    } catch (error) {
      console.error("fetchUserReturns error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch your returns";
      toast.error(`Error fetching returns: ${message}`);
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch details of a specific return
 */
export const fetchUserReturnDetail = createAsyncThunk(
  "returnsUser/fetchUserReturnDetail",
  async (returnId, { rejectWithValue }) => {
    if (!returnId) {
      console.error("fetchUserReturnDetail: returnId is undefined or null");
      return rejectWithValue("Return ID is required");
    }

    try {
      console.log(
        `fetchUserReturnDetail: Fetching details for return ID ${returnId}`
      );
      const data = await returnService.getReturnById(returnId);
      console.log("fetchUserReturnDetail: Success", data);
      return data;
    } catch (error) {
      console.error("fetchUserReturnDetail: Error", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch return detail";
      toast.error(`Error fetching return detail: ${message}`);
      return rejectWithValue(message);
    }
  }
);

/**
 * Request a return for an order
 */
export const requestReturn = createAsyncThunk(
  "returnsUser/requestReturn",
  async ({ orderId, reason, items }, { rejectWithValue }) => {
    try {
      const data = await orderService.requestReturn(orderId, reason, items);
      toast.success("Return request submitted successfully!");
      return data; // The new return object
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit return request";
      toast.error(`Error requesting return: ${message}`);
      return rejectWithValue(message);
    }
  }
);

/**
 * Mark a return as shipped
 */
export const markReturnShipped = createAsyncThunk(
  "returnsUser/markReturnShipped",
  async ({ returnId, trackingInfo }, { rejectWithValue }) => {
    try {
      const data = await returnService.markShippedByUser(
        returnId,
        trackingInfo
      );
      toast.success("Return marked as shipped successfully!");
      return data; // Updated return object
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to mark return as shipped";
      toast.error(`Error marking return as shipped: ${message}`);
      return rejectWithValue(message);
    }
  }
);

// Initial State
const initialState = {
  returns: [], // List of user's returns
  pagination: null,
  currentReturn: null, // Currently viewed return
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  actionStatus: "idle", // For tracking individual action status
  actionError: null,
};

// Returns Slice
const returnsUserSlice = createSlice({
  name: "returnsUser",
  initialState,
  reducers: {
    resetReturnState: (state) => {
      state.status = "idle";
      state.error = null;
      state.actionStatus = "idle";
      state.actionError = null;
      state.currentReturn = null;
    },
    resetCurrentReturn: (state) => {
      state.currentReturn = null;
      state.actionStatus = "idle";
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Returns
      .addCase(fetchUserReturns.pending, (state) => {
        console.log("fetchUserReturns.pending - Setting state to loading");
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserReturns.fulfilled, (state, action) => {
        console.log(
          "fetchUserReturns.fulfilled - Setting returns data",
          action.payload
        );
        state.status = "succeeded";
        state.returns = action.payload.returns;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUserReturns.rejected, (state, action) => {
        console.log(
          "fetchUserReturns.rejected - Setting error",
          action.payload
        );
        state.status = "failed";
        state.error = action.payload;
      })
      // Fetch User Return Detail
      .addCase(fetchUserReturnDetail.pending, (state) => {
        state.status = "loading";
        state.currentReturn = null;
        state.error = null;
      })
      .addCase(fetchUserReturnDetail.fulfilled, (state, action) => {
        console.log(
          "fetchUserReturnDetail.fulfilled - Got return data:",
          action.payload
        );
        state.status = "succeeded";
        state.currentReturn = action.payload;
      })
      .addCase(fetchUserReturnDetail.rejected, (state, action) => {
        console.error(
          "fetchUserReturnDetail.rejected - Error:",
          action.payload
        );
        state.status = "failed";
        state.error = action.payload;
      })
      // Request Return
      .addCase(requestReturn.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(requestReturn.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        // We don't need to update the returns list here as we'll navigate away
        // and reload the list when viewing returns
      })
      .addCase(requestReturn.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.actionError = action.payload;
      })
      // Mark Return Shipped
      .addCase(markReturnShipped.pending, (state) => {
        state.actionStatus = "loading";
        state.actionError = null;
      })
      .addCase(markReturnShipped.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        state.currentReturn = action.payload;
        // Update the return in the list if it exists
        const index = state.returns.findIndex(
          (r) => r.id === action.payload.id
        );
        if (index !== -1) {
          state.returns[index] = action.payload;
        }
      })
      .addCase(markReturnShipped.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.actionError = action.payload;
      });
  },
});

export const { resetReturnState, resetCurrentReturn } =
  returnsUserSlice.actions;

export default returnsUserSlice.reducer;
